import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react'
import { GrClose } from "react-icons/gr";
import GaugeChart from "react-gauge-chart";
import HelperService from '../../../../utility/HelperService';
import { SkeletonBar } from '../../../../components/Skeleton/Skeleton';
import '../../../../components/AppModal/ModalKit.css';
import './AllocationPopup.css';

/* fallback palette for asset-class dots when the API omits colours */
const AA_FALLBACK = ["#16a085", "#ff8a00", "#e6b800", "#0066ff", "#03c4dd", "#b556d7", "#e5484d"];
const DESIRED_C = "#7b5aec";  // Desired allocation bar
const CURRENT_C = "#ff8a00";  // Current allocation bar

/* risk-o-meter: same gauge lib as the dashboard. Names are configurable per firm on
   5-, 6- and 7-level scales, so map by KEYWORD (very / moderately + conservative / moderate
   / aggressive) to a 0–1 needle position rather than matching an exact fixed list. */
const RISK_COLORS = ["#2e9e6b", "#7cc576", "#e6b800", "#ff8a00", "#e5484d"];
const riskColorAt = (p: number) => p < 0.2 ? RISK_COLORS[0] : p < 0.4 ? RISK_COLORS[1] : p < 0.6 ? RISK_COLORS[2] : p < 0.8 ? RISK_COLORS[3] : RISK_COLORS[4];
const riskToPercent = (r: any): number | null => {
    const s = String(r ?? "").toLowerCase().trim();
    if (!s) return null;
    if (/very\s+conservative|highly\s+conservative/.test(s)) return 0.06;
    if (/very\s+aggressive|highly\s+aggressive/.test(s)) return 0.94;
    if (/moderately\s+conservative/.test(s)) return 0.30;
    if (/moderately\s+aggressive/.test(s)) return 0.70;
    if (/conservative|cautious|defensive/.test(s)) return 0.15;
    if (/aggressive|adventurous/.test(s)) return 0.85;
    if (/moderate|balanced|growth/.test(s)) return 0.50;
    const n = Number(s.replace(/[^0-9.]/g, ""));
    if (!isNaN(n) && n > 0) return n <= 1 ? n : n <= 100 ? n / 100 : null;
    return null;
};

/* risk-o-meter — same react-gauge-chart gauge as the Client Dashboard Risk Profile
   card (thick rounded arcs + slim needle), so the two read identically. */
const RiskGauge = ({ percent }: { percent: number }) => {
    const p = Math.max(0, Math.min(1, percent));
    return (
        <GaugeChart
            id="goal-alloc-risk-gauge"
            nrOfLevels={5}
            arcWidth={0.42}
            arcPadding={0.01}
            cornerRadius={2}
            colors={RISK_COLORS}
            percent={p}
            needleColor="#097ab1"
            needleBaseColor="#097ab1"
            hideText={true}
            animate={false}
            style={{ width: "100%" }}
        />
    );
};

const toNum = (v: any) => Number(String(v ?? 0).replace(/,/g, "")) || 0;
const pct = (v: any) => `${+(+toNum(v)).toFixed(1)}%`;

const CircularProgressbarDisplay = (props: any) => {
    const close = () => props.setPIChartPopup(false);

    const dLabels: any[] = props.doughnutLabelData || [];
    const dData: any[] = props.doughnutData || [];
    const dColors: any[] = props.doughnutBackgroundColorData || [];
    const cData: any[] = props.doughnutData2 || [];

    // doughNut (desired) and doughNut2 (current) are PARALLEL arrays in the same fixed
    // asset-class order (Debt, Equity, Bullion, Real Estate, International Equity). The API
    // labels carry the % value baked in ("Debt 30%") and doughNut2 can append an extra
    // "No Data To Display" entry — so read the two datasets POSITIONALLY off the desired
    // labels and strip the trailing "NN%" to recover the clean class name (no dedupe games).
    const cleanLabel = (l: any) => String(l ?? "").replace(/\s*[\d.,]+\s*%\s*$/, "").replace(/\s+/g, " ").trim();
    const isPlaceholder = (l: any) => { const s = cleanLabel(l).toLowerCase(); return !s || /^(no data|no records?|not available|n\/?a|none)/.test(s); };
    const series: any[] = dLabels
        .map((l: any, i: number) => ({
            label: cleanLabel(l),
            color: dColors[i] || AA_FALLBACK[i % AA_FALLBACK.length],
            desired: toNum(dData[i]),
            current: toNum(cData[i]),
        }))
        .filter((s: any) => !isPlaceholder(s.label));

    const hasAlloc = series.some((s: any) => s.desired > 0 || s.current > 0);

    // risk-o-meter position
    const rp = riskToPercent(props.riskProfile);
    const riskKnown = rp != null;
    const riskColor = riskKnown ? riskColorAt(rp as number) : "var(--ap-ink)";

    const earmarked = props.assetEarmarkedList || [];
    let futureTotal = 0;

    return (
        <Transition.Root show={props.pIChartPopup} as={Fragment}>
            <Dialog as="div" className="relative z-[1050]" initialFocus={props.cancelButtonRef} onClose={props.setPIChartPopup}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-[#1c1f3e]/45 backdrop-blur-[2px] transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-[1050] overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-3">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300" enterFrom="opacity-0 translate-y-2 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-2 sm:scale-95"
                        >
                            <Dialog.Panel className="allocpop fpm-panel fpm-xwide">
                                <button type="button" className="fpm-x" title="Close" onClick={close}>
                                    <GrClose size={15} />
                                </button>
                                <div className="fpm-modal">
                                    {/* head */}
                                    <div className="fpm-head">
                                        <div className="fpm-badge">
                                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3a9 9 0 1 0 9 9h-9z" /><path d="M12 3v9h9a9 9 0 0 0-9-9z" /></svg>
                                        </div>
                                        <Dialog.Title as="h2">{props.goalName || "Asset Allocation"}</Dialog.Title>
                                        <p>Desired vs current allocation{props.isshowTable ? " · earmarked assets" : ""}</p>
                                    </div>

                                    <div className="fpm-body">
                                        {props.loading ? (
                                        <div className="ap-loading">
                                            {/* info tiles skeleton */}
                                            <div className="ap-stats">
                                                {[0, 1, 2].map((i) => (
                                                    <div className="ap-stat" key={i}>
                                                        <SkeletonBar w={"70%"} h={11} />
                                                        <SkeletonBar w={"55%"} h={16} style={{ marginTop: 9 }} />
                                                    </div>
                                                ))}
                                            </div>
                                            {/* risk gauge + bars skeleton */}
                                            <div className="ap-main">
                                                <div className="ap-risk">
                                                    <SkeletonBar w={80} h={11} />
                                                    <SkeletonBar w={"82%"} h={92} r={12} style={{ margin: "14px auto 0" }} />
                                                    <SkeletonBar w={"60%"} h={15} style={{ margin: "12px auto 0" }} />
                                                </div>
                                                <div className="ap-bars">
                                                    <SkeletonBar w={150} h={13} />
                                                    <div className="ap-vbars" style={{ marginTop: 18 }}>
                                                        {[0, 1, 2, 3, 4].map((i) => (
                                                            <div className="ap-vcol" key={i}>
                                                                <div className="ap-vplot" style={{ display: "flex", alignItems: "flex-end", justifyContent: "center", gap: 6, height: 130 }}>
                                                                    <SkeletonBar w={20} h={`${45 + ((i * 13) % 45)}%`} r={4} />
                                                                    <SkeletonBar w={20} h={`${30 + ((i * 17) % 45)}%`} r={4} />
                                                                </div>
                                                                <SkeletonBar w={"75%"} h={10} style={{ margin: "9px auto 0" }} />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            {/* earmarked table skeleton */}
                                            {props.isshowTable &&
                                                <>
                                                    <div className="ap-sec" style={{ marginTop: 18 }}>Earmarked Assets</div>
                                                    <div className="ap-earwrap">
                                                        <table className="ap-tbl">
                                                            <thead>
                                                                <tr>
                                                                    <th>Asset Earmarked</th>
                                                                    <th className="ap-num">Earmarked %</th>
                                                                    <th className="ap-num">Current Value ₹</th>
                                                                    <th className="ap-num">Future Value ₹</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {[0, 1, 2, 3].map((i) => (
                                                                    <tr key={i}>
                                                                        <td><SkeletonBar w={`${55 + ((i * 11) % 30)}%`} h={11} /></td>
                                                                        <td className="ap-num"><SkeletonBar w={42} h={11} style={{ marginLeft: "auto" }} /></td>
                                                                        <td className="ap-num"><SkeletonBar w={72} h={11} style={{ marginLeft: "auto" }} /></td>
                                                                        <td className="ap-num"><SkeletonBar w={72} h={11} style={{ marginLeft: "auto" }} /></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            }
                                        </div>
                                        ) : (
                                        <>
                                        {/* goal info tiles */}
                                        <div className="ap-stats">
                                            <div className="ap-stat">
                                                <span className="ap-k">Amount required at maturity</span>
                                                <span className="ap-v">{props.goal_maturity_required > 0 ? "₹" + HelperService.getCurrencyFormatterWithoutDecimal(props.goal_maturity_required) : "NA"}</span>
                                            </div>
                                            <div className="ap-stat">
                                                <span className="ap-k">Maturity date</span>
                                                <span className="ap-v">{HelperService.getFormatedDateByDMY(props.goalMaturityDate)}</span>
                                            </div>
                                            <div className="ap-stat">
                                                <span className="ap-k">Tenure</span>
                                                <span className="ap-v">{props.selfTenure ? HelperService.getCurrencyFormatter(props.selfTenure) : "0"} yrs{props.goalNature ? ` · ${props.goalNature}` : ""}</span>
                                            </div>
                                        </div>

                                        {/* risk-o-meter (left) + allocation graph box (right) */}
                                        <div className="ap-main">
                                            <div className="ap-risk">
                                                <span className="ap-risk-k">Risk Profile</span>
                                                <div className="ap-risk-body">
                                                    {riskKnown ?
                                                        <>
                                                            <div className="ap-gauge"><RiskGauge percent={rp as number} /></div>
                                                            <span className="ap-risk-v" style={{ color: riskColor }}>{props.riskProfile}</span>
                                                        </>
                                                        : <span className="ap-risk-v">{props.riskProfile ? props.riskProfile : "Not set"}</span>
                                                    }
                                                </div>
                                            </div>
                                            <div className="ap-bars">
                                                <div className="ap-graph-head">
                                                    <span className="ap-sec">Asset Allocation</span>
                                                    <div className="ap-bars-legend">
                                                        <span><i style={{ background: DESIRED_C }} />Desired <span style={{ color: "var(--ap-muted)", fontWeight: 600 }}>(risk profile)</span></span>
                                                        <span><i style={{ background: CURRENT_C }} />Current <span style={{ color: "var(--ap-muted)", fontWeight: 600 }}>(valuation)</span></span>
                                                    </div>
                                                </div>
                                                {hasAlloc ?
                                                    <div className="ap-vbars">
                                                        {series.map((s: any, i: number) => (
                                                            <div className="ap-vcol" key={i}>
                                                                <div className="ap-vplot">
                                                                    <div className="ap-vbar" style={{ height: Math.min(s.desired, 100) + "%", background: DESIRED_C }} title={`Desired ${pct(s.desired)}`}><span className="ap-vval">{pct(s.desired)}</span></div>
                                                                    <div className="ap-vbar" style={{ height: Math.min(s.current, 100) + "%", background: CURRENT_C }} title={`Current ${pct(s.current)}`}><span className="ap-vval">{pct(s.current)}</span></div>
                                                                </div>
                                                                <div className="ap-vlabel"><span className="ap-dot" style={{ background: s.color }} /><span>{s.label}</span></div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    : <div className="ap-empty">No allocation data available.</div>
                                                }
                                            </div>
                                        </div>

                                        {/* earmarked assets */}
                                        {props.isshowTable &&
                                            <>
                                                <div className="ap-sec">Earmarked Assets</div>
                                                <div className="ap-earwrap">
                                                    <table className="ap-tbl">
                                                        <thead>
                                                            <tr>
                                                                <th>Asset Earmarked</th>
                                                                <th className="ap-num">Earmarked %</th>
                                                                <th className="ap-num">Current Value ₹</th>
                                                                <th className="ap-num">Future Value ₹</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {earmarked.length > 0 && earmarked.map((res: any, index: any) => {
                                                                futureTotal += parseFloat(res.maturity_amount) || 0;
                                                                return (
                                                                    <tr key={index}>
                                                                        <td>{res.asset_name}</td>
                                                                        <td className="ap-num">{HelperService.getCurrencyFormatter(res.percent_mapping)}%</td>
                                                                        <td className="ap-num">₹{HelperService.getCurrencyFormatterWithoutDecimal(res.current_valuation)}</td>
                                                                        <td className="ap-num">₹{HelperService.getCurrencyFormatterWithoutDecimal(res.maturity_amount)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                            {earmarked.length > 0 &&
                                                                <tr className="ap-tot">
                                                                    <td colSpan={3} style={{ textAlign: "right" }}>Total</td>
                                                                    <td className="ap-num">₹{HelperService.getCurrencyFormatterWithoutDecimal(futureTotal)}</td>
                                                                </tr>
                                                            }
                                                            {earmarked.length === 0 &&
                                                                <tr><td colSpan={4} className="ap-empty">No records found</td></tr>
                                                            }
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </>
                                        }
                                        </>
                                        )}
                                    </div>

                                    {/* footer */}
                                    <div className="fpm-foot">
                                        <button type="button" className="fpm-btn" onClick={close} ref={props.cancelButtonRef}>Close</button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};
export default CircularProgressbarDisplay;
